import React from 'react';

/**
 * Status / priority chips, mapped onto the brand status tokens (tokens.css,
 * docs/product/CLOUDSAFE_BRANDING.md §4.2). Status colour is a meaning, used consistently:
 *
 *   success  green  — completed, approved, won, paid, accepted
 *   info     blue   — enquiry, quoted, sent, issued
 *   live     solid brand blue — active ("On site")
 *   teal            — scheduled, invoiced
 *   category purple — invited
 *   warning  amber  — (no built-in status; use tone="warning" for due-soon/expiring)
 *   danger   red    — overdue
 *   neutral  grey   — draft, closed;  cancelled = grey + strike-through
 *
 * Text colours are the -dark variant pushed to ≥4.5:1 on the -light background.
 * Hex values (not CSS vars) so the palettes also work in canvas/PDF and in
 * screens that read them. Every chip always renders its text label — colour is
 * never the only signal.
 */
export const TONES = {
  success: { bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0', mark: '#22c55e' },
  info:    { bg: '#EBF4FF', fg: '#1565C0', border: '#bfdbfe', mark: '#1A80E5' },
  live:    { bg: '#1A80E5', fg: '#ffffff', border: '#1A80E5', mark: '#1A80E5' },
  teal:    { bg: '#f0fdfa', fg: '#0f766e', border: '#99f6e4', mark: '#14b8a6' },
  purple:  { bg: '#f5f3ff', fg: '#6d28d9', border: '#ddd6fe', mark: '#8b5cf6' },
  warning: { bg: '#fffbeb', fg: '#92400e', border: '#fde68a', mark: '#f59e0b' },
  danger:  { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca', mark: '#ef4444' },
  neutral: { bg: '#f3f4f6', fg: '#374151', border: '#e5e7eb', mark: '#9ca3af' },
  muted:   { bg: '#f3f4f6', fg: '#6b7280', border: '#e5e7eb', mark: '#9ca3af' },
};

const entry = (tone, label, extra) => {
  const t = TONES[tone];
  return Object.assign([t.bg, t.fg, label, t.border], { tone, ...(extra || {}) });
};

/**
 * STATUS_PALETTE[key] = [bg, fg, label, border] (+ .tone, .strike on the array).
 * Destructure `[bg, fg, label]` as before.
 */
export const STATUS_PALETTE = {
  invited:     entry('purple', 'Invited'),
  enquiry:     entry('info', 'Enquiry'),
  quoted:      entry('info', 'Quoted'),
  sent:        entry('info', 'Sent'),
  approved:    entry('success', 'Approved'),
  won:         entry('success', 'Won'),
  paid:        entry('success', 'Paid'),
  invoiced:    entry('teal', 'Invoiced'),
  scheduled:   entry('teal', 'Scheduled'),
  active:      entry('live', 'On site'),
  completed:   entry('success', 'Completed'),
  draft:       entry('neutral', 'Draft'),
  overdue:     entry('danger', 'Overdue'),
  // Permit-to-Work lifecycle
  issued:      entry('info', 'Issued'),
  accepted:    entry('success', 'Accepted'),
  closed:      entry('neutral', 'Closed'),
  cancelled:   entry('muted', 'Cancelled', { strike: true }),
  lost:        entry('muted', 'Lost'),
  expired:     entry('muted', 'Expired'),
  rejected:    entry('danger', 'Rejected'),
  void:        entry('muted', 'Void', { strike: true }),
  written_off: entry('warning', 'Written off'),
  received:    entry('teal', 'Received'),
};

/** Spelling / synonym aliases → canonical key. "Inquiry" is always shown as "Enquiry". */
const STATUS_ALIASES = {
  inquiry: 'enquiry', enquiries: 'enquiry', inquiries: 'enquiry',
  in_progress: 'active', 'in progress': 'active', 'on site': 'active', onsite: 'active', on_site: 'active',
  complete: 'completed', done: 'completed',
  canceled: 'cancelled',
};

export const normaliseStatus = (s) => {
  const k = String(s ?? '').trim().toLowerCase();
  return STATUS_ALIASES[k] || k.replace(/\s+/g, '_');
};

/** Human label for a status key ("active" → "On site", "inquiry" → "Enquiry"). */
export const statusLabel = (s) => {
  const e = STATUS_PALETTE[normaliseStatus(s)];
  if (e) return e[2];
  const raw = String(s ?? '').replace(/_/g, ' ').trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
};

/** Solid marker colour for a status (calendar blocks, list accents, map pins). */
export const statusMark = (s) => {
  const e = STATUS_PALETTE[normaliseStatus(s)];
  return (e && TONES[e.tone].mark) || TONES.neutral.mark;
};

/** Marker colours keyed by status — use instead of per-screen MARK_BY_STATUS maps. */
export const MARK_BY_STATUS = Object.fromEntries(Object.keys(STATUS_PALETTE).map((k) => [k, statusMark(k)]));

/** Asset status set (B1). */
export const ASSET_STATUS_PALETTE = {
  'Overdue':        [TONES.danger.bg, TONES.danger.fg],
  'Part Complete':  [TONES.warning.bg, TONES.warning.fg],
  'Complete':       [TONES.success.bg, TONES.success.fg],
  'Scheduled':      [TONES.info.bg, TONES.info.fg],
  'Decommissioned': [TONES.muted.bg, TONES.muted.fg],
  'Unknown':        [TONES.muted.bg, TONES.muted.fg],
  'Not Applicable': ['#f9fafb', TONES.muted.fg],
  'Not Seen':       [TONES.purple.bg, TONES.purple.fg],
};

/** Priority palette (G2 — separate from status). */
export const PRIORITY_PALETTE = {
  low:    [TONES.teal.bg, TONES.teal.fg, 'Low'],
  medium: [TONES.info.bg, TONES.info.fg, 'Medium'],
  high:   [TONES.warning.bg, '#b45309', 'High'],
  urgent: [TONES.danger.bg, TONES.danger.fg, 'Urgent'],
};

/** Generic chip primitive — pass explicit bg/fg or a `tone`. */
export const Chip = ({ bg, fg, border, tone, label, dot = false, strike = false, style = {}, title, className = '' }) => {
  const t = tone ? TONES[tone] : null;
  const background = bg || t?.bg || TONES.muted.bg;
  const color = fg || t?.fg || TONES.muted.fg;
  const borderColor = border || t?.border || 'transparent';
  return (
    <span
      className={`cw-chip ${className}`.trim()}
      title={title}
      style={{ background, color, borderColor, ...style }}
    >
      {dot && <span className="cw-chip-mark" aria-hidden="true" />}
      <span style={strike ? { textDecoration: 'line-through' } : undefined}>{label}</span>
    </span>
  );
};

const resolve = (palette, key, fallbackLabel) => {
  const e = palette[key] || palette[String(key).toLowerCase()] || palette[normaliseStatus(key)];
  if (e) return { bg: e[0], fg: e[1], label: e[2] || fallbackLabel || String(key), border: e[3], strike: !!e.strike };
  return { bg: TONES.muted.bg, fg: TONES.muted.fg, label: fallbackLabel || statusLabel(key) || String(key), border: TONES.muted.border, strike: false };
};

/**
 * <StatusChip /> — the workspace's status/priority/asset pill.
 * Exactly one of `status | priority | asset` is expected.
 *   <StatusChip status="active" />       → dot, solid blue "On site"
 *   <StatusChip status="inquiry" />      → "Enquiry" (alias)
 *   <StatusChip priority="urgent" />     → no dot, red "Urgent"
 *   <StatusChip asset="Part Complete" /> → dot, amber
 *   <StatusChip tone="warning" label="Expiring" />  → brand tone, custom label
 *   <StatusChip label="Custom" bg=.. fg=.. />       → escape hatch
 * `label` is only a fallback for keys the palette doesn't know (built-in labels win).
 */
const StatusChip = ({ status, priority, asset, label, bg, fg, tone, dot, style, title }) => {
  if (status != null) {
    const r = resolve(STATUS_PALETTE, status, label);
    return <Chip bg={r.bg} fg={r.fg} border={r.border} label={r.label} strike={r.strike} dot={dot ?? true} style={style} title={title} />;
  }
  if (asset != null) {
    const r = resolve(ASSET_STATUS_PALETTE, asset, label);
    return <Chip bg={r.bg} fg={r.fg} label={r.label || asset} dot={dot ?? true} style={style} title={title} />;
  }
  if (priority != null) {
    const r = resolve(PRIORITY_PALETTE, priority, label);
    return <Chip bg={r.bg} fg={r.fg} label={r.label} dot={dot ?? false} style={style} title={title} />;
  }
  return <Chip bg={bg} fg={fg} tone={tone || (!bg && !fg ? 'muted' : undefined)} label={label} dot={dot ?? false} style={style} title={title} />;
};

export default StatusChip;
